import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import StripeConnectBanner from "../../components/StripeConnectBanner";
import PortalDocumentHub from "../../components/PortalDocumentHub";
import DealForgeLogo, { DealForgeIcon } from "../../components/DealForgeLogo";
import ClosingChecklistManager from "../../components/ClosingChecklistManager";
import ClosingCostEstimator from "../../components/ClosingCostEstimator";
import {
  PortalSidebar,
  PortalEmptyState,
  QuickDeployAction,
  ViewHeader,
} from "../../components/PortalShared";
import ExchangeWidget from "../../components/ExchangeWidget";
import {
  Shield,
  LogOut,
  Plus,
  DollarSign,
  FileText,
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowRight,
  Menu,
  X,
  Settings,
  AlertTriangle,
  CheckCircle,
  Search,
  Calendar,
  Home,
  Users,
  Briefcase,
  FileSearch,
  ShoppingBag,
  Target,
  Zap,
  Activity,
  Radio,
  ChevronRight,
  Wallet,
  FileSignature,
  CalendarDays
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SettingsProfileView({ profile, fields, onSave }) {
  const [form, setForm] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { if (onSave) await onSave(form); toast.success("Profile updated"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <ViewHeader title="Configuration" subtitle="Manage your portal profile and settings" />
      <div className="max-w-lg bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-4">
        {fields.map(({ key, label, type = 'text' }) => (
          <div key={key}>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">{label}</label>
            {type === 'textarea' ? (
              <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} rows={3} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50" />
            ) : (
              <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            )}
          </div>
        ))}
        <button onClick={handleSave} disabled={saving} className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold text-sm rounded transition-all disabled:opacity-50" data-testid="save-settings-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Title View Components ─────────────────────────────────────────────────────

function TitleOrdersView({ orders, setActiveView }) {
  const stageColor = { received: 'text-blue-400', searching: 'text-indigo-400', clearing: 'text-purple-400', commitment: 'text-[#D4AF37]', closing: 'text-amber-400', complete: 'text-[#00FF88]' };
  return (
    <div>
      <ViewHeader title="Active Orders" subtitle="All title orders currently in your queue" count={orders.length} />
      {orders.length === 0 ? (
        <PortalEmptyState icon={FileText} title="No Active Orders" desc="Accept orders from the marketplace to populate your queue" action="Browse Marketplace" onAction={() => setActiveView('marketplace')} />
      ) : (
        <div className="space-y-3">
          {orders.map((order, i) => (
            <div key={order.order_id || i} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{order.property_address || 'Title Order'}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                    <span>{order.order_type || 'Standard Search'}</span>
                    <span>{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</span>
                    {order.priority === 'rush' && <span className="text-red-400 font-medium">RUSH</span>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{formatCurrency(order.fee || order.order_fee || 0)}</p>
                  <span className={`text-xs font-semibold capitalize ${stageColor[order.executionStage] || 'text-white/40'}`}>{order.executionStage?.replace('_', ' ') || 'Active'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitleMarketplaceView({ orders, setActiveView }) {
  return (
    <div>
      <ViewHeader title="Order Marketplace" subtitle="Available title orders you can accept" count={orders.filter(o => o.status === 'available' || !o.accepted).length} />
      {orders.filter(o => o.status === 'available').length === 0 ? (
        <PortalEmptyState icon={ShoppingBag} title="No Available Orders" desc="New title orders from investors will appear here for you to accept" />
      ) : (
        <div className="space-y-3">
          {orders.filter(o => o.status === 'available').map((order) => (
            <div key={order.order_id || order._id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 hover:border-[#00FF88]/30 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{order.property_address || 'New Order'}</p>
                  <p className="text-white/40 text-xs mt-0.5">{order.order_type || 'Title Search'}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{formatCurrency(order.fee || 0)}</p>
                  <button onClick={() => toast.info("Accept order functionality — coming soon")} className="mt-2 text-xs px-4 py-1.5 bg-[#00FF88]/10 hover:bg-[#00FF88]/20 border border-[#00FF88]/30 text-[#00FF88] font-medium rounded transition-all">
                    Accept Order
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitleEscrowView({ orders }) {
  const escrowOrders = orders.filter(o => o.escrow_amount || o.executionStage === 'closing');
  const totalEscrow = escrowOrders.reduce((s, o) => s + (o.escrow_amount || 0), 0);
  return (
    <div>
      <ViewHeader title="Deal Wallet" subtitle="Active deal wallets and pending disbursements" count={escrowOrders.length} />
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-[#0A0A0A] border border-[#00FF88]/20 rounded-xl p-5 text-center">
          <p className="text-2xl font-bold text-[#00FF88]">{formatCurrency(totalEscrow)}</p>
          <p className="text-white/40 text-xs mt-1">Total in Deal Wallets</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-5 text-center">
          <p className="text-2xl font-bold text-[#D4AF37]">{escrowOrders.length}</p>
          <p className="text-white/40 text-xs mt-1">Active Accounts</p>
        </div>
      </div>
      {escrowOrders.length === 0 ? (
        <PortalEmptyState icon={Wallet} title="No Deal Wallets" desc="Deal wallets will appear here when orders reach the closing stage" />
      ) : (
        <div className="space-y-2">
          {escrowOrders.map((o) => (
            <div key={o.order_id || o._id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
              <p className="text-white text-sm">{o.property_address || 'Deal Wallet'}</p>
              <div className="flex items-center gap-3">
                <p className="text-white font-bold">{formatCurrency(o.escrow_amount || 0)}</p>
                <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded">Pending Closing</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitleSearchesView({ orders }) {
  const searching = orders.filter(o => ['searching', 'clearing'].includes(o.executionStage));
  return (
    <div>
      <ViewHeader title="Title Searches" subtitle="Orders currently in the search and clearing phase" count={searching.length} />
      {searching.length === 0 ? (
        <PortalEmptyState icon={FileSearch} title="No Active Searches" desc="Orders in the search phase will appear here" />
      ) : (
        <div className="space-y-3">
          {searching.map((o) => (
            <div key={o.order_id || o._id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{o.property_address || 'Title Search'}</p>
                  <p className="text-white/40 text-xs mt-0.5">Stage: {o.executionStage?.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-center">
                    <p className="text-white/60 text-xs">{o.daysInStage || '--'}</p>
                    <p className="text-white/30 text-[10px]">days</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 border rounded ${o.executionStage === 'searching' ? 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20' : 'text-purple-400 bg-purple-500/10 border-purple-500/20'}`}>
                    {o.executionStage}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitleClosingsView({ orders }) {
  const closing = orders.filter(o => o.executionStage === 'closing' || o.executionStage === 'commitment');
  return (
    <div>
      <ViewHeader title="Closing Pipeline" subtitle="Orders approaching or in the closing stage" count={closing.length} />
      {closing.length === 0 ? (
        <PortalEmptyState icon={Calendar} title="No Upcoming Closings" desc="Orders in the closing and commitment stages will appear here" />
      ) : (
        <div className="space-y-3">
          {closing.map((o) => (
            <div key={o.order_id || o._id} className="bg-[#0A0A0A] border border-amber-500/20 rounded-xl p-5 flex items-start justify-between">
              <div>
                <p className="text-white font-medium text-sm">{o.property_address || 'Closing'}</p>
                <p className="text-white/40 text-xs mt-0.5">{o.order_type || 'Title Closing'}</p>
                {o.closing_date && <p className="text-amber-400 text-xs mt-1">Closing: {new Date(o.closing_date).toLocaleDateString()}</p>}
              </div>
              <div className="text-right">
                <p className="text-white font-bold">{formatCurrency(o.sale_price || o.escrow_amount || 0)}</p>
                <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded">Closing</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitleOpportunitiesView({ orders }) {
  return (
    <div>
      <ViewHeader title="Deal Opportunities" subtitle="New and recent title order opportunities" count={orders.length} />
      {orders.length === 0 ? (
        <PortalEmptyState icon={Target} title="No Opportunities" desc="Title order opportunities from the network will appear here" />
      ) : (
        <div className="space-y-2">
          {orders.slice(0, 20).map((o) => (
            <div key={o.order_id || o._id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between hover:border-[#00FF88]/20 transition-all">
              <div>
                <p className="text-white text-sm font-medium">{o.property_address || 'Title Order'}</p>
                <p className="text-white/40 text-xs mt-0.5">{o.order_type || 'Standard'} • {o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</p>
              </div>
              <p className="text-white font-bold">{formatCurrency(o.fee || o.order_fee || 0)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TitleRevenueView({ orders, stats }) {
  const completed = orders.filter(o => o.executionStage === 'complete');
  const totalRevenue = completed.reduce((s, o) => s + (o.fee || o.order_fee || 0), 0);
  return (
    <div>
      <ViewHeader title="Revenue Tracking" subtitle="Fees collected from completed title orders" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Revenue', value: formatCurrency(totalRevenue), color: 'text-[#00FF88]', border: 'border-emerald-500/20' },
          { label: 'Orders Complete', value: completed.length, color: 'text-[#D4AF37]', border: 'border-[#D4AF37]/20' },
          { label: 'Active Orders', value: orders.filter(o => o.executionStage !== 'complete').length, color: 'text-blue-400', border: 'border-blue-500/20' },
        ].map(({ label, value, color, border }) => (
          <div key={label} className={`bg-[#0A0A0A] border ${border} rounded-xl p-5 text-center`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TitleAnalyticsView({ orders, stats }) {
  return (
    <div>
      <ViewHeader title="Performance Analytics" subtitle="Title operations and throughput metrics" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Orders', value: orders.length, icon: FileText, color: 'text-blue-400' },
          { label: 'In Progress', value: orders.filter(o => !['complete', 'cancelled'].includes(o.executionStage)).length, icon: Clock, color: 'text-[#D4AF37]' },
          { label: 'Completed', value: orders.filter(o => o.executionStage === 'complete').length, icon: CheckCircle, color: 'text-[#00FF88]' },
          { label: 'Avg Turnaround', value: `${stats?.avg_turnaround || '--'} days`, icon: TrendingUp, color: 'text-purple-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 text-center">
            <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-white/30 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Title Order Execution Stages ─────────────────────────────────────────────
const ORDER_STAGES = [
  { key: 'received', label: 'RECEIVED', color: '#3B82F6' },
  { key: 'searching', label: 'SEARCHING', color: '#6366F1' },
  { key: 'clearing', label: 'CLEARING', color: '#8B5CF6' },
  { key: 'commitment', label: 'COMMITMENT', color: '#D4AF37' },
  { key: 'closing', label: 'CLOSING', color: '#F59E0B' },
  { key: 'complete', label: 'COMPLETE', color: '#10B981' },
];

// Map order status to stages
const STATUS_TO_STAGE = {
  'received': 'received',
  'in_progress': 'searching',
  'title_search': 'searching',
  'clearing_issues': 'clearing',
  'commitment_issued': 'commitment',
  'scheduled_closing': 'closing',
  'closed': 'complete',
  'complete': 'complete'
};

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// System Status Badge
function SystemStatusBadge({ status }) {
  const configs = {
    stable: { color: 'emerald', text: 'OPTIMAL', bgClass: 'bg-emerald-500/[0.08]', borderClass: 'border-emerald-500/20', textClass: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' },
    alert: { color: 'amber', text: 'ATTENTION', bgClass: 'bg-amber-500/[0.08]', borderClass: 'border-amber-500/20', textClass: 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' },
    bottleneck: { color: 'red', text: 'BOTTLENECK', bgClass: 'bg-red-500/[0.08]', borderClass: 'border-red-500/20', textClass: 'text-[#FF3B30]' }
  };
  const config = configs[status] || configs.stable;
  
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 ${config.bgClass} border ${config.borderClass} rounded`}>
      <Shield className={`w-3 h-3 ${config.textClass}`} />
      <span className={`text-[9px] font-semibold ${config.textClass} uppercase tracking-wider`}>{config.text}</span>
    </div>
  );
}

// Global Metric Display
function GlobalMetric({ label, value, trend, positive }) {
  return (
    <div className="text-center">
      <p className="text-[9px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-2 justify-center">
        <span className="text-xl font-semibold text-white tracking-tight">{value}</span>
        {trend && (
          <span className={`text-[10px] font-medium ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

// Execution Flow Stage
function ExecutionStage({ stage, count, volume, avgDays, isActive, onClick }) {
  const velocityIcon = avgDays < 3 ? TrendingUp : avgDays > 7 ? TrendingDown : Activity;
  const VelocityIcon = velocityIcon;
  
  return (
    <button
      onClick={onClick}
      className={`relative flex-1 p-4 border-t-2 transition-all ${
        isActive 
          ? 'bg-white/[0.03] border-t-emerald-400' 
          : 'hover:bg-white/[0.02] border-t-transparent'
      }`}
      style={{ borderTopColor: isActive ? stage.color : 'transparent' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: stage.color }}>
          {stage.label}
        </span>
        <VelocityIcon className={`w-3 h-3 ${
          avgDays < 3 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : avgDays > 7 ? 'text-[#FF3B30]' : 'text-white/40'
        }`} />
      </div>
      <div className="text-2xl font-bold text-white mb-1">{count}</div>
      <div className="text-[10px] text-white/40">
        {formatCurrency(volume)} value
      </div>
      {avgDays > 0 && (
        <div className="text-[9px] text-white/30 mt-1">
          ~{avgDays.toFixed(1)}d avg
        </div>
      )}
    </button>
  );
}

// Order Row
function OrderRow({ order, onClick }) {
  const stage = ORDER_STAGES.find(s => s.key === order.executionStage) || ORDER_STAGES[0];
  
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] hover:border-emerald-500/30 transition-all"
    >
      {/* Stage Indicator */}
      <div 
        className="w-1 h-12 rounded-full" 
        style={{ backgroundColor: stage.color }}
      />
      
      {/* Property Info */}
      <div className="flex-1 text-left">
        <p className="text-white font-medium text-sm">{order.property_address}</p>
        <p className="text-white/40 text-xs mt-0.5">
          {order.order_type || 'Title Order'} • {order.client_name || 'Client'}
        </p>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-white font-mono text-sm">{formatCurrency(order.fee || order.purchase_price)}</p>
          <p className="text-[10px] text-white/40">Order Value</p>
        </div>
        <div className="text-right">
          <p className={`font-mono text-sm ${order.daysInStage > 5 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-white/70'}`}>
            {order.daysInStage}d
          </p>
          <p className="text-[10px] text-white/40">In Stage</p>
        </div>
        <div className="text-right">
          <span className={`text-[10px] px-2 py-1 rounded ${
            order.priority === 'high' ? 'bg-red-500/20 text-[#FF3B30]' :
            order.priority === 'rush' ? 'bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' :
            'bg-white/[0.08]/50 text-white/50'
          }`}>
            {(order.priority || 'normal').toUpperCase()}
          </span>
        </div>
        <ChevronRight className="w-4 h-4 text-white/30" />
      </div>
    </button>
  );
}

// ─── Title Nav Sections ────────────────────────────────────────────────────────
const TITLE_NAV = [
  {
    title: "Operations",
    items: [
      { icon: Target, label: "Command Center", view: "dashboard" },
      { icon: ShoppingBag, label: "Deal Opportunities", view: "opportunities" },
      { icon: FileText, label: "Active Orders", view: "orders" },
      { icon: ShoppingBag, label: "Marketplace", view: "marketplace" },
    ]
  },
  {
    title: "Execution",
    items: [
      { icon: Wallet, label: "Deal Wallet", view: "escrow" },
      { icon: FileSearch, label: "Title Searches", view: "searches" },
      { icon: Calendar, label: "Closings", view: "closings" },
      { icon: CalendarDays, label: "Deal Calendar", url: "/calendar?from=title" },
      { icon: Users, label: "Service Requests", view: "requests" },
    ]
  },
  {
    title: "Tools",
    items: [
      { icon: CheckCircle, label: "Closing Checklists", view: "closing-checklists" },
      { icon: DollarSign, label: "Cost Estimator", view: "cost-estimator" },
    ]
  },
  {
    title: "Intelligence",
    items: [
      { icon: DollarSign, label: "Revenue Stream", view: "revenue" },
      { icon: TrendingUp, label: "Performance", view: "analytics" },
      { icon: FileSignature, label: "Documents", view: "documents" },
      { icon: Settings, label: "Configuration", view: "settings" },
    ]
  }
];

export default function TitleCommandCenter({ user, loading: authLoading }) {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState('stable');
  const [isAdminViewing, setIsAdminViewing] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [notifyCounts, setNotifyCounts] = useState({});
  const [checklistData, setChecklistData] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Wait for auth check to complete before making decisions
    if (authLoading) {
      return; // Still checking auth, wait
    }
    
    // If admin is viewing without title company session, show preview mode immediately
    if (user?.is_admin) {
      setIsAdminViewing(true);
      setCompany({ company_name: 'Admin Preview', email: user.email });
      setStats({ total_orders: 0, pending_orders: 0, completed_orders: 0, revenue: 0 });
      setOrders([]);
      setLoading(false);
      return;
    }
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin, user?.email, authLoading]);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, ordersRes, checklistsRes, notifyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/title/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/title/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/title/orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/portal-tools/closing-checklists`, { withCredentials: true }).catch(() => ({ data: { checklists: [] } })),
        axios.get(`${BACKEND_URL}/api/portal-tools/notification-counts`, { withCredentials: true }).catch(() => ({ data: { counts: {} } })),
      ]);

      setCompany(profileRes.data);
      setStats(statsRes.data);
      setIsAdminViewing(false);
      setChecklistData(checklistsRes.data?.checklists || []);
      setNotifyCounts(notifyRes.data?.counts || {});
      
      const transformedOrders = (ordersRes.data || []).map(order => ({
        ...order,
        executionStage: STATUS_TO_STAGE[order.status] || 'received',
        daysInStage: Math.floor((Date.now() - new Date(order.updated_at || order.created_at).getTime()) / (1000 * 60 * 60 * 24))
      }));
      setOrders(transformedOrders);

      const staleOrders = transformedOrders.filter(o => o.daysInStage > 7).length;
      if (staleOrders > 5) setSystemStatus('bottleneck');
      else if (staleOrders > 0) setSystemStatus('alert');
      else setSystemStatus('stable');

    } catch (error) {
      if (user?.is_admin) {
        setIsAdminViewing(true);
        setCompany({ company_name: 'Admin Preview', email: user.email });
        setStats({ total_orders: 0, pending_orders: 0, completed_orders: 0, revenue: 0 });
        setOrders([]);
      } else if (error.response?.status === 401) {
        navigate("/title");
      } else {
        toast.error("Failed to load command center");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/title/auth/logout`, {}, { withCredentials: true });
      navigate("/title");
    } catch (error) {
      navigate("/title");
    }
  };

  // Calculate stage metrics
  const getStageMetrics = () => {
    return ORDER_STAGES.map(stage => {
      const stageOrders = orders.filter(order => order.executionStage === stage.key);
      const volume = stageOrders.reduce((sum, order) => sum + (order.fee || order.purchase_price || 0), 0);
      const avgDays = stageOrders.length > 0 
        ? stageOrders.reduce((sum, order) => sum + order.daysInStage, 0) / stageOrders.length 
        : 0;
      return {
        ...stage,
        count: stageOrders.length,
        volume,
        avgDays
      };
    });
  };

  // Global metrics
  const globalMetrics = {
    activeOrders: orders.filter(o => o.executionStage !== 'complete').length,
    closingToday: orders.filter(o => o.executionStage === 'closing').length,
    totalVolume: orders.reduce((sum, o) => sum + (o.purchase_price || 0), 0),
    revenueEarned: stats?.revenue_mtd || orders.filter(o => o.executionStage === 'complete').reduce((sum, o) => sum + (o.fee || 0), 0)
  };

  const stageMetrics = getStageMetrics();

  // Filter orders by active stage
  const filteredOrders = activeStage 
    ? orders.filter(order => order.executionStage === activeStage)
    : orders.filter(o => o.executionStage !== 'complete').slice(0, 5);

  // Canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || orders.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (ORDER_STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / ORDER_STAGES.length),
        y: canvas.height / 2 + (Math.random() - 0.5) * 30,
        targetX: (stageIdx + 1.5) * (canvas.width / ORDER_STAGES.length),
        progress: 0,
        speed: 0.003 + Math.random() * 0.003,
        color: ORDER_STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      ORDER_STAGES.forEach((stage, idx) => {
        if (idx < ORDER_STAGES.length - 1) {
          const x1 = (idx + 0.5) * (canvas.width / ORDER_STAGES.length);
          const x2 = (idx + 1.5) * (canvas.width / ORDER_STAGES.length);
          ctx.beginPath();
          ctx.moveTo(x1, canvas.height / 2);
          ctx.lineTo(x2, canvas.height / 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.02)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      if (Math.random() < 0.02 && particles.length < 12) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 8;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.4 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [orders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <PortalSidebar
        navSections={TITLE_NAV}
        activeView={activeView}
        setActiveView={setActiveView}
        homeLink="/title/dashboard"
        portal="Title"
        entity={company}
        entityInitial="T"
        entitySubtitle={company?.state || 'Title Company'}
        accent="emerald"
        onLogout={handleLogout}
        notifyCounts={notifyCounts}
        currentRole="title"
        userRoles={user?.extra_roles || []}
        isAdmin={user?.is_admin || false}
      />
      
      <main className="ml-16 lg:ml-64 transition-all">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Branding + Status */}
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    Title <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">Command</span>
                  </h1>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.15em]">Order Operations</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                    <div className="relative w-2 h-2">
                      <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                      <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                  </div>
                  
                  <SystemStatusBadge status={systemStatus} />
                </div>
              </div>

              {/* Center: Key Metrics */}
              <div className="flex items-center gap-8">
                <GlobalMetric 
                  label="Active Orders" 
                  value={globalMetrics.activeOrders}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Closing Today" 
                  value={globalMetrics.closingToday}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Total Volume" 
                  value={`$${(globalMetrics.totalVolume / 1000000).toFixed(2)}M`}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Revenue MTD" 
                  value={formatCurrency(globalMetrics.revenueEarned)}
                  trend="+5.2%"
                  positive
                />
              </div>

              {/* Right: Time + Action */}
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-white/50 font-mono">
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-lg font-mono font-bold text-white">
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Link to="/title/marketplace">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-10 px-5">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Marketplace
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Execution Flow Visualization */}
        <section className="border-b border-white/[0.06] relative">
          <canvas 
            ref={canvasRef} 
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
          <div className="relative z-10 flex">
            {stageMetrics.map((stage, idx) => (
              <ExecutionStage
                key={stage.key}
                stage={stage}
                count={stage.count}
                volume={stage.volume}
                avgDays={stage.avgDays}
                isActive={activeStage === stage.key}
                onClick={() => setActiveStage(activeStage === stage.key ? null : stage.key)}
              />
            ))}
          </div>
        </section>

        {/* Stripe Connect Banner */}
        <div className="px-6 pt-4">
          <StripeConnectBanner 
            partnerType="title" 
            apiPrefix="/api/deal-wallet"
            compact={true}
          />
        </div>

        {/* Main Content Grid */}
        <div className="p-6">
          {(() => {
            switch(activeView) {
              case 'opportunities': return <TitleOpportunitiesView orders={orders} />;
              case 'orders': return <TitleOrdersView orders={orders} setActiveView={setActiveView} />;
              case 'marketplace': return <TitleMarketplaceView orders={orders} setActiveView={setActiveView} />;
              case 'escrow': return <TitleEscrowView orders={orders} />;
              case 'searches': return <TitleSearchesView orders={orders} />;
              case 'closings': return <TitleClosingsView orders={orders} />;
              case 'requests': return <PortalEmptyState icon={FileText} title="Signature Requests" desc="Documents sent for signature will appear here" />;
              case 'revenue': return <TitleRevenueView orders={orders} stats={stats} />;
              case 'analytics': return <TitleAnalyticsView orders={orders} stats={stats} />;
              case 'closing-checklists': return <ClosingChecklistManager checklists={checklistData} />;
              case 'cost-estimator': return <ClosingCostEstimator />;
              case 'documents': return <PortalDocumentHub portalType="title" />;
              case 'settings': return (
                <SettingsProfileView
                  profile={company}
                  fields={[
                    { key: 'company_name', label: 'Company Name' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'license_number', label: 'License Number' },
                    { key: 'service_area', label: 'Service Area' },
                  ]}
                />
              );
              default: return (
                <>
                  {/* Exchange Opportunities Widget — top priority */}
                  <ExchangeWidget role="title" user={user} className="mb-6" />

                  <div className="grid grid-cols-3 gap-6">
                  <div className="col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                        {activeStage ? `${ORDER_STAGES.find(s => s.key === activeStage)?.label} Orders` : 'Active Orders'}
                      </h2>
                      {activeStage && <button onClick={() => setActiveStage(null)} className="text-[10px] text-white/40 hover:text-white">Clear Filter</button>}
                    </div>
                    <div className="space-y-2">
                      {filteredOrders.length > 0 ? (
                        filteredOrders.map((order) => (
                          <OrderRow key={order.order_id} order={order} onClick={() => setActiveView('orders')} />
                        ))
                      ) : (
                        <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06]">
                          <FileText className="w-12 h-12 text-white/30 mx-auto mb-3" />
                          <p className="text-white/40">No orders in this stage</p>
                          <button onClick={() => setActiveView('marketplace')} className="text-[#00FF88] text-sm hover:underline mt-2">Browse marketplace for new orders</button>
                        </div>
                      )}
                    </div>
                    {filteredOrders.length > 0 && (
                      <button onClick={() => setActiveView('orders')} className="flex items-center justify-center gap-2 py-3 text-sm text-white/50 hover:text-[#00FF88] transition-all duration-300 w-full">
                        View All Orders <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-4">
                    <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Quick Deploy</h2>
                    <div className="space-y-2">
                      <QuickDeployAction icon={ShoppingBag} label="Browse Marketplace" description="Find new title orders" variant="primary" onClick={() => setActiveView('marketplace')} />
                      <QuickDeployAction icon={FileSearch} label="Start Title Search" description="Begin new search" onClick={() => setActiveView('searches')} />
                      <QuickDeployAction icon={Calendar} label="Schedule Closing" description="Set closing date" onClick={() => setActiveView('closings')} />
                      <QuickDeployAction icon={FileText} label="Issue Commitment" description="Generate title commitment" onClick={() => setActiveView('orders')} />
                    </div>
                    {orders.filter(o => o.priority === 'rush' || o.daysInStage > 7).length > 0 && (
                      <div className="bg-amber-500/[0.08] border border-amber-500/30 p-4 mt-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[#FF9F0A] font-medium text-sm">{orders.filter(o => o.priority === 'rush' || o.daysInStage > 7).length} Orders Need Attention</p>
                            <p className="text-white/40 text-xs mt-1">Rush or stale orders require action</p>
                            <button onClick={() => setActiveView('orders')} className="text-[#FF9F0A] text-xs hover:underline mt-2 inline-flex items-center gap-1">
                              Review Now <ArrowRight className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] p-4 mt-4">
                      <h3 className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-3">Performance MTD</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white/50">Orders Completed</span>
                          <span className="text-sm font-mono text-[#00FF88]">{orders.filter(o => o.executionStage === 'complete').length}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white/50">Revenue Earned</span>
                          <span className="text-sm font-mono font-bold text-white">{formatCurrency(globalMetrics.revenueEarned)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white/50">Avg Turnaround</span>
                          <span className="text-sm font-mono font-bold text-white">{stats?.avg_turnaround || '--'} days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </>
              );
            }
          })()}
        </div>
      </main>
    </div>
  );
}
